import React, { useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search } from "lucide-react";
import { mockArticles } from "@/data/mockArticles";
import { useLanguage } from "@/context/LanguageContext";

const Articles = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const { t } = useLanguage();

  const categories = ["all", ...Array.from(new Set(mockArticles.map(article => article.category)))];
  
  const filteredArticles = mockArticles.filter(article => {
    const matchesSearch = searchQuery === "" || 
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = categoryFilter === "all" || article.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <MainLayout>
      <div className="container py-8">
        <h1 className="text-3xl font-bold mb-6">
          {t('common.rentingGuides') || "租屋攻略"}
        </h1>
        
        <div className="flex flex-col md:flex-row gap-4 md:items-center mb-8">
          <form className="w-full md:max-w-md" onSubmit={(e) => e.preventDefault()}>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜尋文章..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </form>
          
          <div className="w-full md:w-auto">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="文章分類" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {categories.map(category => (
                    <SelectItem key={category} value={category}>
                      {category === "all" ? "所有分類" : category}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>

        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map(article => (
              <Link to={`/articles/${article.id}`} key={article.id}>
                <div className="overflow-hidden rounded-lg border bg-card transition-colors hover:bg-accent/50 h-full flex flex-col">
                  <div className="aspect-video w-full overflow-hidden bg-muted">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="object-cover w-full h-full transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                  <div className="p-4 flex flex-col flex-grow">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-muted-foreground">
                        {new Date(article.publishDate).toLocaleDateString()}
                      </span>
                      <span className="text-xs px-2 py-1 bg-primary-purple/10 text-primary-purple rounded-full">
                        {article.category}
                      </span>
                    </div>
                    <h3 className="font-bold text-lg mb-2 line-clamp-2">{article.title}</h3>
                    <p className="text-muted-foreground text-sm mb-2 line-clamp-3 flex-grow">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center mt-2">
                      <span className="text-sm font-medium text-primary-purple">繼續閱讀</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-lg text-muted-foreground">沒有找到符合條件的文章</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => {
                setSearchQuery("");
                setCategoryFilter("all");
              }}
            >
              重置搜尋
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Articles;
